import React, { useContext, useState } from 'react';
import ModalContext from '../../../context/ModalContext';
import AuthContext from '../../../context/AuthContext';
import {
  Button,
  CircularProgress,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';

const FIELD_LABELS = {
  name: 'Name',
  logo: 'Logo',
  contact_person: 'Contact person',
  email: 'Email',
  phone: 'Phone',
  background_color: 'Background color',
  locations: 'Locations',
};

const FIELD_ORDER = Object.keys(FIELD_LABELS);

// Step 1: company fields only (no locations)
const COMPANY_FIELD_ORDER = FIELD_ORDER.filter((k) => k !== 'locations');

// Keys the API accepts in fieldChoices
const ALLOWED_FIELD_CHOICE_KEYS = ['contact_person', 'email', 'phone', 'logo', 'background_color'];

const CompanyAlreadyExistsModal = ({ modalDetails }) => {
  const modalCtx = useContext(ModalContext);
  const authCtx = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { existingCompany, leadCompany, misalignedFields = [], coupon } = modalDetails || {};

  const [step, setStep] = useState(1);

  // Per-field choice: 'existing' | 'lead' for allowed misaligned fields (default: existing).
  const [fieldChoices, setFieldChoices] = useState(() => {
    const initial = {};
    (misalignedFields || [])
      .filter((key) => ALLOWED_FIELD_CHOICE_KEYS.includes(key))
      .forEach((key) => {
        initial[key] = 'existing';
      });
    return initial;
  });

  // Lead locations user chose to add to the final list (copies of location objects)
  const [selectedLeadLocations, setSelectedLeadLocations] = useState([]);

  const handleFieldChoice = (fieldKey, value) => {
    setFieldChoices((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const existingLocations = Array.isArray(existingCompany?.locations) ? existingCompany.locations : [];
  const leadLocations = Array.isArray(leadCompany?.locations) ? leadCompany.locations : [];

  const addLeadLocation = (loc) => {
    const title = loc.title ?? '';
    const address = loc.address ?? '';
    const alreadyAdded = selectedLeadLocations.some(
      (l) => l.title === title && l.address === address
    );
    if (alreadyAdded) return;
    setSelectedLeadLocations((prev) => [...prev, { ...loc, title, address }]);
  };

  const removeSelectedLocation = (index) => {
    setSelectedLeadLocations((prev) => prev.filter((_, i) => i !== index));
  };

  const closeHandle = () => {
    modalCtx.closeModal();
  };

  const handleProceed = async () => {
    if (!coupon?.id) return;
    const existingCompanyId = existingCompany?.id;
    if (existingCompanyId == null) {
      setError('Existing company ID is missing. Cannot proceed.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const locations = selectedLeadLocations.map((loc) => ({
        id: loc.id ?? null,
        title: loc.title ?? '',
        address: loc.address ?? '',
      }));

      const filteredFieldChoices = Object.fromEntries(
        Object.entries(fieldChoices).filter(([key]) => ALLOWED_FIELD_CHOICE_KEYS.includes(key))
      );

      const response = await fetch(
        `${process.env.REACT_APP_API_URI}/v3/advertising/coupon/${coupon.id}/existingCompany/live`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authCtx.token}`,
          },
          body: JSON.stringify({
            existingCompanyId,
            fieldChoices: filteredFieldChoices,
            locations,
          }),
        }
      );

      const data = await response.json();

      if (data.status === 'success') {
        modalCtx.setPageUpdate({ page: 'CouponLeads', couponId: coupon.id });
        modalCtx.closeModal();
      } else {
        setError(data.error || data.message || 'Failed to make coupon live. Please try again.');
      }
    } catch (err) {
      console.error('Error making coupon live:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isMisaligned = (field) => misalignedFields && misalignedFields.includes(field);

  const renderCellValue = (value, fieldKey) => {
    if (fieldKey === 'logo') {
      return value ? (
        <img src={value} alt="Logo" style={{ maxHeight: 40, maxWidth: 80, objectFit: 'contain' }} />
      ) : (
        <Typography variant="body2" color="text.secondary">—</Typography>
      );
    }
    if (fieldKey === 'locations' && Array.isArray(value)) {
      if (!value.length) return <Typography variant="body2" color="text.secondary">—</Typography>;
      return (
        <Box component="ul" sx={{ m: 0, pl: 1.5, fontSize: '0.875rem' }}>
          {value.map((loc, i) => (
            <li key={i}>{loc.title || 'Location'}: {loc.address || '—'}</li>
          ))}
        </Box>
      );
    }
    return (
      <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
        {value != null && value !== '' ? String(value) : '—'}
      </Typography>
    );
  };

  const misalignedCompanyFields = COMPANY_FIELD_ORDER.filter((k) => isMisaligned(k));
  const resolveableFields = misalignedCompanyFields.filter((k) => ALLOWED_FIELD_CHOICE_KEYS.includes(k));
  const infoOnlyFields = misalignedCompanyFields.filter((k) => !ALLOWED_FIELD_CHOICE_KEYS.includes(k));
  const matchingFieldCount = COMPANY_FIELD_ORDER.length - misalignedCompanyFields.length;

  return (
    <div className="modal-container company-already-exists-modal" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div className="modal-header" style={{ flexShrink: 0 }}>
        <h1>Company already exists</h1>
      </div>

      <Stepper activeStep={step - 1} sx={{ px: 3, py: 2, flexShrink: 0 }}>
        <Step><StepLabel>Resolve field conflicts</StepLabel></Step>
        <Step><StepLabel>Configure locations</StepLabel></Step>
      </Stepper>

      <div className="modal-body" style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {step === 1 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A company with a similar name already exists. Choose whether to keep the{' '}
              <strong>existing company</strong> value or use the <strong>lead/coupon</strong> value
              for each differing field, then click Next to configure locations.
            </Typography>

            {misalignedCompanyFields.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                All fields match — no conflicts to resolve. Click <strong>Next</strong> to configure locations.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {matchingFieldCount > 0
                    ? `${matchingFieldCount} field${matchingFieldCount !== 1 ? 's' : ''} match. Review and resolve ${misalignedCompanyFields.length} difference${misalignedCompanyFields.length !== 1 ? 's' : ''} below.`
                    : `Review and resolve ${misalignedCompanyFields.length} difference${misalignedCompanyFields.length !== 1 ? 's' : ''} below.`}
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, width: 140 }}>Field</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Existing company</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Lead / coupon data</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: 200 }}>Use this value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resolveableFields.map((fieldKey) => {
                        const label = FIELD_LABELS[fieldKey];
                        const existingVal = existingCompany?.[fieldKey];
                        const leadVal = leadCompany?.[fieldKey];
                        const choice = fieldChoices[fieldKey] ?? 'existing';
                        return (
                          <TableRow key={fieldKey} sx={{ bgcolor: 'action.hover' }}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>{label}</Typography>
                            </TableCell>
                            <TableCell>{renderCellValue(existingVal, fieldKey)}</TableCell>
                            <TableCell>{renderCellValue(leadVal, fieldKey)}</TableCell>
                            <TableCell>
                              <FormControl component="fieldset" size="small">
                                <RadioGroup
                                  row
                                  value={choice}
                                  onChange={(e) => handleFieldChoice(fieldKey, e.target.value)}
                                >
                                  <FormControlLabel value="existing" control={<Radio size="small" />} label="Existing" />
                                  <FormControlLabel value="lead" control={<Radio size="small" />} label="Lead" />
                                </RadioGroup>
                              </FormControl>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {infoOnlyFields.map((fieldKey) => {
                        const label = FIELD_LABELS[fieldKey];
                        const existingVal = existingCompany?.[fieldKey];
                        const leadVal = leadCompany?.[fieldKey];
                        return (
                          <TableRow key={fieldKey}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>{label}</Typography>
                            </TableCell>
                            <TableCell>{renderCellValue(existingVal, fieldKey)}</TableCell>
                            <TableCell>{renderCellValue(leadVal, fieldKey)}</TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">Existing kept</Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            <Typography variant="body2" color="text.secondary">
              Click <strong>Next</strong> to configure locations, then proceed with migration.
            </Typography>
          </>
        )}

        {step === 2 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review locations. Existing company locations are always included. You can add any lead/coupon locations below.
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Existing company locations
            </Typography>
            <List dense component={Paper} variant="outlined" sx={{ mb: 2 }}>
              {existingLocations.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No locations" secondary="—" />
                </ListItem>
              ) : (
                existingLocations.map((loc, i) => (
                  <ListItem key={i}>
                    <ListItemText primary={loc.title || 'Location'} secondary={loc.address || '—'} />
                  </ListItem>
                ))
              )}
            </List>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Lead / coupon locations — add to include
            </Typography>
            {leadLocations.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No lead locations. You can still proceed with existing company locations only.
              </Typography>
            ) : (
              <List dense component={Paper} variant="outlined" sx={{ mb: 2 }}>
                {leadLocations.map((loc, i) => (
                  <ListItem
                    key={i}
                    secondaryAction={
                      <IconButton edge="end" aria-label="Add" onClick={() => addLeadLocation(loc)} size="small">
                        <AddIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={loc.title || 'Location'} secondary={loc.address || '—'} />
                  </ListItem>
                ))}
              </List>
            )}

            {selectedLeadLocations.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Added from lead ({selectedLeadLocations.length})
                </Typography>
                <List dense component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  {selectedLeadLocations.map((loc, i) => (
                    <ListItem
                      key={i}
                      secondaryAction={
                        <IconButton edge="end" aria-label="Remove" onClick={() => removeSelectedLocation(i)} size="small">
                          <RemoveIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={loc.title || 'Location'} secondary={loc.address || '—'} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            <Typography variant="body2" color="text.secondary">
              Click <strong>Proceed with migration</strong> to apply your choices and make the coupon live.
            </Typography>
          </>
        )}
      </div>
      <div className="modal-footer" style={{ flexShrink: 0 }}>
        {step === 1 ? (
          <>
            <Button className="cancel-btn" onClick={closeHandle}>
              Cancel
            </Button>
            <Button onClick={() => setStep(2)}>
              Next
            </Button>
          </>
        ) : (
          <>
            <Button className="cancel-btn" onClick={() => setStep(1)} disabled={loading}>
              Back
            </Button>
            <Button onClick={handleProceed} disabled={loading}>
              {loading ? (
                <><CircularProgress size={16} style={{ color: '#fff', marginRight: 8 }} />Publishing...</>
              ) : 'Proceed with migration'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default CompanyAlreadyExistsModal;
